import { UNDERWRITING_STATUS_CONFIG } from '@shared';
import Divider from '@mui/material/Divider';

interface AiUnderwritingGuidanceResultsProps {
  parsedResults: {
    eligibilityOverview: {
      status: string;
      content: string[];
    };
    flaggedItems: string[];
    recommendations: string[];
  };
}

export const AiUnderwritingGuidanceResultComponent = ({
  parsedResults,
}: AiUnderwritingGuidanceResultsProps) => {
  const getStatusBadge = (status: string) => {
    // Map status labels to config keys
    const statusKey = Object.keys(UNDERWRITING_STATUS_CONFIG).find(
      key =>
        UNDERWRITING_STATUS_CONFIG[key as keyof typeof UNDERWRITING_STATUS_CONFIG].label === status,
    ) as keyof typeof UNDERWRITING_STATUS_CONFIG | undefined;

    if (!statusKey) {
      return (
        <span className="rounded-full bg-yellow-100 p-2 text-xs font-medium text-yellow-800">
          {status}
        </span>
      );
    }

    const config = UNDERWRITING_STATUS_CONFIG[statusKey];
    return (
      <span
        className={`rounded-full p-2 text-xs font-medium ${config.classes.bgColor} ${config.classes.textColor}`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-6 pb-2">
      {/* Disclaimer */}
      <div className="rounded-lg bg-gray-50 p-4">
        <div className="text-primary-dark text-xs">
          <p className="mb-2">
            <span className="font-semibold">Disclaimer:</span> This analysis is generated by a
            Wealth Assurance Group AI tool, <span className="font-semibold">not</span> by the
            insurance carrier. It is for informational purposes only and does not guarantee your
            final underwriting outcome.
          </p>
          <ul className="ml-4 list-disc space-y-1 pl-2">
            <li className="pl-1">
              <span className="font-semibold">AI can make mistakes.</span> You are responsible for
              verifying these insights.
            </li>
            <li className="pl-1">
              <span className="font-semibold">This is not an offer of coverage.</span> All final
              eligibility and pricing decisions are made exclusively by the carrier's underwriters.
            </li>
            <li className="pl-1">
              <span className="font-semibold">These insights are not advice.</span> They are
              informational observations based upon what is known by Wealth Assurance Group about
              the applicant and the carrier's eligibility guidelines, it is not professional
              insurance, financial, medical, or legal advice.
            </li>
          </ul>
        </div>
      </div>

      {/* Eligibility Overview Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <h3 className="text-primary-dark text-lg font-medium">Eligibility Overview</h3>
          {getStatusBadge(parsedResults.eligibilityOverview.status)}
        </div>

        <div className="space-y-3">
          {parsedResults.eligibilityOverview.content.map((item, index) => (
            <p
              key={index}
              className="text-primary-dark text-sm"
            >
              {item}
            </p>
          ))}
        </div>
      </div>
      <Divider />

      {/* Flagged Items Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <h3 className="text-primary-dark text-lg font-medium">Flagged Items</h3>
          {!parsedResults.flaggedItems[0]?.includes('None') && (
            <span
              className={`rounded-full p-2 text-xs font-medium ${UNDERWRITING_STATUS_CONFIG.unsure.classes.bgColor} ${UNDERWRITING_STATUS_CONFIG.unsure.classes.textColor}`}
            >
              {UNDERWRITING_STATUS_CONFIG.unsure.label}
            </span>
          )}
        </div>
        {parsedResults.flaggedItems.length > 0 ? (
          <ul className="space-y-3">
            {parsedResults.flaggedItems.map((item, index) => {
              const hasSource = item.includes('Source:');
              const parts = hasSource ? item.split('Source:') : [item];
              return (
                <li
                  key={index}
                  className="flex items-start gap-4"
                >
                  <span className="text-primary-dark h-2 w-2 flex-shrink-0 rounded-full">•</span>
                  <div className="flex flex-col gap-1">
                    <span className="text-primary-dark text-sm">{parts[0].trim()}</span>
                    {hasSource && (
                      <>
                        <Divider />
                        <span className="text-xs italic text-gray-700">Source:{parts[1]}</span>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div>None</div>
        )}
      </div>
      <Divider />

      {/* Recommendations Section */}
      <div className="space-y-4">
        <h3 className="text-primary-dark text-lg font-medium">Recommendations</h3>
        {parsedResults.recommendations.length > 0 ? (
          <ul className="space-y-3">
            {parsedResults.recommendations.map((item, index) => (
              <li
                key={index}
                className="flex items-start gap-4"
              >
                <span className="text-primary-dark h-2 w-2 flex-shrink-0 rounded-full">•</span>
                <span className="text-primary-dark text-sm">{item}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div>None</div>
        )}
      </div>
    </div>
  );
};
